/**
 * TextareaField — multi-line text input. Auto-grows with content (min 80px,
 * max 240px), then scrolls.
 *
 * Same recipe as TextField (borderless soft-elevated, teal focus glow +
 * animated gradient underline, uppercase eyebrow label). The shell wraps a
 * bare `<textarea>` (no inner box ring) so the only focus chrome is the
 * shell's glow + underline.
 */

import { motion, useReducedMotion } from "framer-motion";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

export interface TextareaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  label?: string;
  helper?: ReactNode;
  error?: string;
  /** Minimum visible height in px (default 80). */
  minHeight?: number;
  /** Maximum auto-grow height in px before scroll (default 240). */
  maxHeight?: number;
}

export const TextareaField = forwardRef<
  HTMLTextAreaElement,
  TextareaFieldProps
>(function TextareaField(
  {
    id: idProp,
    label,
    helper,
    error,
    required,
    disabled,
    minHeight = 80,
    maxHeight = 240,
    onFocus,
    onBlur,
    onChange,
    style,
    value,
    defaultValue,
    ...rest
  },
  ref,
) {
  // Always link the label to the field, even when the caller passes no id.
  const autoId = useId();
  const id = idProp ?? autoId;
  const innerRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);
  const reduce = useReducedMotion();
  const [focused, setFocused] = useState(false);

  function resize(el: HTMLTextAreaElement) {
    el.style.height = "auto";
    const h = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }

  useEffect(() => {
    const el = innerRef.current;
    if (el) resize(el);
    // Re-run when controlled `value` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div style={{ width: "100%" }}>
      {label ? (
        <label
          htmlFor={id}
          style={{
            display: "block",
            font: "600 12px/1 var(--font-sans)",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: error ? "var(--color-danger)" : "var(--color-muted)",
            marginBottom: 6,
          }}
        >
          {label}
          {required ? (
            <span
              aria-hidden
              style={{ color: "var(--color-danger)", marginLeft: 4 }}
            >
              *
            </span>
          ) : null}
        </label>
      ) : null}

      <div
        style={{
          position: "relative",
          background: disabled
            ? "var(--color-surface-alt)"
            : focused
              ? "var(--color-input-focus)"
              : "var(--color-input)",
          borderRadius: "var(--radius-md)",
          boxShadow: error
            ? "var(--shadow-glow-danger)"
            : focused
              ? "var(--shadow-glow-focus)"
              : "var(--field-rest)",
          transition:
            "box-shadow var(--duration-base) var(--ease-smooth), background-color var(--duration-base) var(--ease-smooth)",
          overflow: "hidden",
        }}
      >
        <textarea
          ref={innerRef}
          id={id}
          required={required}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id ?? rest.name}-error` : undefined}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          onChange={(e) => {
            resize(e.currentTarget);
            onChange?.(e);
          }}
          style={{
            display: "block",
            width: "100%",
            minHeight,
            background: "transparent",
            border: 0,
            outline: 0,
            resize: "none",
            paddingInline: 14,
            paddingBlock: 12,
            color: disabled ? "var(--color-faint)" : "var(--color-primary)",
            font: "400 var(--text-base)/1.5 var(--font-sans)",
            caretColor: "var(--color-brand-teal-600)",
            ...style,
          }}
          {...rest}
        />
        <motion.span
          aria-hidden
          initial={false}
          animate={{ scaleX: focused && !error ? 1 : 0 }}
          transition={{ duration: reduce ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background: "var(--field-underline)",
            transformOrigin: "left",
            pointerEvents: "none",
          }}
        />
      </div>

      {error ? (
        <p
          role="alert"
          style={{
            marginTop: 6,
            font: "500 12px/1.4 var(--font-sans)",
            color: "var(--color-danger)",
          }}
        >
          {error}
        </p>
      ) : helper ? (
        <p
          style={{
            marginTop: 6,
            font: "400 12px/1.4 var(--font-sans)",
            color: "var(--color-muted)",
          }}
        >
          {helper}
        </p>
      ) : null}
    </div>
  );
});
